import { Injectable } from '@angular/core';
import { hasActionsExecuting } from '@ngxs-labs/actions-executing';
import { Select, Store } from '@ngxs/store';
import { PROGRAM_BANNER_STATE_TOKEN } from '~backoffice/app/~shared/constants/global.constants';
import {
  AddBanner,
  LoadBanners,
  RemoveBanner,
  ResetState,
  SaveBanner,
  SelectBanner
} from '~backoffice/app/~store/actions/program-banner.actions';
import { ProgramBannerState } from '~backoffice/app/~store/states/program-banner.state';
import { isEqual } from 'lodash-es';
import { Observable, combineLatest, distinctUntilChanged, map } from 'rxjs';
import { Banner } from '~core/models/core.models';

@Injectable()
export class ProgramBannerFacade {
  @Select(ProgramBannerState.banners) private stateBanners$!: Observable<
    ReturnType<typeof ProgramBannerState.banners>
  >;

  banners$!: Observable<ReturnType<typeof ProgramBannerState.banners>>;

  @Select(ProgramBannerState.bannerLoadError) bannerLoadError$!: Observable<
    ReturnType<typeof ProgramBannerState.bannerLoadError>
  >;

  @Select(ProgramBannerState.selectedBanner)
  private stateSelectedBanner$!: Observable<
    ReturnType<typeof ProgramBannerState.selectedBanner>
  >;

  selectedBanner$!: Observable<
    ReturnType<typeof ProgramBannerState.selectedBanner>
  >;

  @Select(ProgramBannerState.selectedBannerPropertyPath)
  selectedBannerPropertyPath$!: Observable<
    ReturnType<typeof ProgramBannerState.selectedBannerPropertyPath>
  >;

  @Select(ProgramBannerState.canSaveSelectedBanner)
  canSaveSelectedBanner$!: Observable<
    ReturnType<typeof ProgramBannerState.canSaveSelectedBanner>
  >;

  @Select(hasActionsExecuting([LoadBanners]))
  readonly isLoadingBanner$!: Observable<boolean>;

  @Select(hasActionsExecuting([RemoveBanner]))
  readonly isRemovingBanner$!: Observable<boolean>;

  @Select(hasActionsExecuting([SaveBanner]))
  readonly isSavingBanner$!: Observable<boolean>;

  canUserInteract$!: Observable<boolean>;

  constructor(private store: Store) {
    this.banners$ = this.stateBanners$.pipe(
      distinctUntilChanged((a, b) =>
        isEqual(
          a?.map(el => el.id),
          b?.map(el => el.id)
        )
      )
    );

    this.selectedBanner$ = this.stateSelectedBanner$.pipe(
      distinctUntilChanged((a, b) => a?.id === b?.id)
    );

    this.canUserInteract$ = combineLatest([
      this.isRemovingBanner$,
      this.isSavingBanner$
    ]).pipe(
      map(
        ([isRemovingBanner, isSavingBanner]) =>
          !isRemovingBanner && !isSavingBanner
      )
    );
  }

  // Action Dispatchers
  resetState(
    ...params: ConstructorParameters<typeof ResetState>
  ): Observable<unknown> {
    return this.store.dispatch(new ResetState(...params));
  }

  loadBanners(
    ...params: ConstructorParameters<typeof LoadBanners>
  ): Observable<unknown> {
    return this.store.dispatch(new LoadBanners(...params));
  }

  addBanner(
    ...params: ConstructorParameters<typeof AddBanner>
  ): Observable<unknown> {
    return this.store.dispatch(new AddBanner(...params));
  }

  removeBanner(
    ...params: ConstructorParameters<typeof RemoveBanner>
  ): Observable<unknown> {
    return this.store.dispatch(new RemoveBanner(...params));
  }

  saveBanner(
    ...params: ConstructorParameters<typeof SaveBanner>
  ): Observable<unknown> {
    return this.store.dispatch(new SaveBanner(...params));
  }

  selectBanner(
    ...params: ConstructorParameters<typeof SelectBanner>
  ): Observable<unknown> {
    return this.store.dispatch(new SelectBanner(...params));
  }

  // Snapshots
  getCreatedBanners(): Banner[] | undefined {
    return this.store
      .selectSnapshot(PROGRAM_BANNER_STATE_TOKEN)
      .banners?.filter(el => !!el.data.id)
      .map(el => el.data as Banner);
  }
}
